/**
 * Bulk Assignment Service
 * Generates date series and creates multiple assignments in sequence
 */

import { addWeeks, addMonths, addDays, format, parseISO, isBefore } from 'date-fns'
import { supabase } from '../lib/supabase'
import { calculateAssignmentStatusV2 } from './assignmentStatusCalculatorV2'
import { logAssignmentChange } from './activityLog'
import type { AssignmentDataV2 } from './assignmentStatusCalculatorV2'

// =============================================
// TYPES
// =============================================

export type RecurrenceType = 'weekly' | 'biweekly' | 'triweekly' | 'monthly'
export type EndMode = 'count' | 'endDate'

export interface ArticleItem {
  articleId: string
  quantity: number
  customPrice?: number
  notes?: string
}

export interface BulkAssignmentRow {
  id: string
  title: string
  description: string
  start_date: string
  start_time: string
  end_time: string
  subcontractor_ids: string[]
  articles: ArticleItem[]
  removed: boolean
}

export interface BulkCreateProgress {
  current: number
  total: number
  success: number
  failed: number
  errors: string[]
}

// =============================================
// DATE GENERATION
// =============================================

/**
 * Generate evenly spaced day offsets within a week for N times per week.
 * E.g. 2x → [0, 3] (Mon+Thu), 3x → [0, 2, 4] (Mon+Wed+Fri)
 */
function weekOffsets(timesPerWeek: number): number[] {
  const offsets: number[] = []
  for (let i = 0; i < timesPerWeek; i++) {
    offsets.push(Math.floor(i * 7 / timesPerWeek))
  }
  return offsets
}

export function generateDates(
  startDate: string,
  recurrence: RecurrenceType,
  endMode: EndMode,
  count?: number,
  endDate?: string,
  timesPerWeek: number = 1
): string[] {
  const dates: string[] = []
  let weekStart = parseISO(startDate)
  const offsets = recurrence === 'weekly' ? weekOffsets(timesPerWeek) : [0]

  if (endMode === 'count' && count) {
    // count = antal veckor/perioder, varje period ger timesPerWeek datum
    for (let i = 0; i < count; i++) {
      for (const offset of offsets) {
        dates.push(format(addDays(weekStart, offset), 'yyyy-MM-dd'))
      }
      if (recurrence === 'weekly') weekStart = addWeeks(weekStart, 1)
      else if (recurrence === 'biweekly') weekStart = addWeeks(weekStart, 2)
      else if (recurrence === 'triweekly') weekStart = addWeeks(weekStart, 3)
      else if (recurrence === 'monthly') weekStart = addMonths(weekStart, 1)
    }
  } else if (endMode === 'endDate' && endDate) {
    const end = parseISO(endDate)
    while (!isBefore(end, weekStart)) {
      for (const offset of offsets) {
        const d = addDays(weekStart, offset)
        if (!isBefore(end, d) || format(d, 'yyyy-MM-dd') <= endDate) {
          dates.push(format(d, 'yyyy-MM-dd'))
        }
      }
      if (recurrence === 'weekly') weekStart = addWeeks(weekStart, 1)
      else if (recurrence === 'biweekly') weekStart = addWeeks(weekStart, 2)
      else if (recurrence === 'triweekly') weekStart = addWeeks(weekStart, 3)
      else if (recurrence === 'monthly') weekStart = addMonths(weekStart, 1)
    }
  }

  return dates
}

// =============================================
// BULK CREATE
// =============================================

export async function createBulkAssignments(
  rows: BulkAssignmentRow[],
  customerId: string,
  location: string,
  onProgress: (progress: BulkCreateProgress) => void
): Promise<BulkCreateProgress> {
  const activeRows = rows.filter(r => !r.removed)
  const progress: BulkCreateProgress = {
    current: 0,
    total: activeRows.length,
    success: 0,
    failed: 0,
    errors: []
  }

  for (const row of activeRows) {
    progress.current++
    onProgress({ ...progress })

    try {
      // Calculate status
      const statusInput: AssignmentDataV2 = {
        status: 'ohanterad',
        start_date: row.start_date || null,
        start_time: row.start_time || null,
        subcontractor_ids: row.subcontractor_ids || [],
        customer_comm_status: 'ej_kontaktad',
        customer_invoice_status: 'ej_fakturerad'
      }
      const calculatedStatus = calculateAssignmentStatusV2(statusInput)

      // Insert assignment
      const { data, error } = await supabase
        .from('assignments')
        .insert({
          title: row.title,
          description: row.description || null,
          customer_id: customerId,
          status: calculatedStatus,
          start_date: row.start_date || null,
          start_time: row.start_time || null,
          end_time: row.end_time || null,
          location: location || null
        })
        .select()
        .single()

      if (error) throw error

      // Add subcontractors
      if (row.subcontractor_ids.length > 0) {
        const subInserts = row.subcontractor_ids.map(subId => ({
          assignment_id: data.id,
          subcontractor_id: subId,
          tg_status: 'preliminar_tilldelad',
          tg_invoice_status: 'ej_fakturerad'
        }))
        await supabase.from('assignment_subcontractors').insert(subInserts)
      }

      // Add articles
      if (row.articles.length > 0) {
        const articleInserts = row.articles.map(item => ({
          assignment_id: data.id,
          article_id: item.articleId,
          quantity: item.quantity,
          custom_price: item.customPrice,
          notes: item.notes
        }))
        await supabase.from('assignment_articles').insert(articleInserts)
      }

      // Log creation
      await logAssignmentChange(data.id, 'created', {
        description: `skapade uppdraget "${row.title}" (serieuppdrag)`
      })

      progress.success++
    } catch (err) {
      progress.failed++
      progress.errors.push(`Rad ${progress.current}: ${err instanceof Error ? err.message : 'Okänt fel'}`)
    }

    onProgress({ ...progress })
  }

  return progress
}
